import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  InputLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { BarLoader } from 'react-spinners';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import moment from 'moment';
import { getPayrollClients } from 'src/services/clientService';
import { getClientWisePayrollsByParams } from 'src/services/payrollService';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { NotificationManager } from 'react-notifications';
import { formatClientId, formatClientLabel } from 'src/utils/helperFunctions';
import ViewPayroll from '../viewPayroll/ViewPayroll';

interface Column {
  id: string; // Changed from union to string to support both sets of columns
  label: string;
  minWidth?: number;
  align?: 'right';
  //   format?: (value: number) => string;
}

const columns: Column[] = [
  { id: 'clientID', label: 'Client ID' },
  { id: 'state', label: 'State' },
  { id: 'legalBusinessName', label: 'Legal Business Name', minWidth: 150 },
  { id: 'doingBusinessAs', label: 'Doing Business As', minWidth: 130 },
  { id: 'monthYear', label: 'Month/Year' },
  { id: 'numberOfEmployees', label: 'Number of Employees' },
  { id: 'totalGrossWages', label: 'Gross Wages' },
  { id: 'totalSocSec', label: 'Soc. Sec.' },
  { id: 'totalMedicare', label: 'Medicare' },
  { id: 'totalFederalWH', label: 'Federal WH' },
  { id: 'tax941', label: '941 Tax' },
  { id: 'totalFuta', label: 'FUTA Tax' },
  { id: 'totalStateWH', label: 'State WH' },
  { id: 'totalSuta', label: 'SUTA Tax' },
  { id: 'Actions', label: 'Actions' }
];

const detailedColumns: Column[] = [
  { id: 'employeeName', label: 'Employee Name' },
  { id: 'federalTaxId', label: 'Federal Tax ID' },
  { id: 'grossWages', label: 'Gross Wages' },
  { id: 'socSec', label: 'Social Security' },
  { id: 'medicare', label: 'Medicare' },
  { id: 'federalWH', label: 'Federal WH' },
  { id: 'stateWH', label: 'State WH' },
  { id: 'localWH', label: 'Local WH' },
  { id: 'tips', label: 'Tips' },
  { id: 'benefitDeduction', label: 'Benefit & Deduction' },
  { id: 'netPay', label: 'Net Pay' },
  { id: 'futaTaxable', label: 'FUTA Taxable Wages' },
  { id: 'futaTax', label: 'FUTA Tax' },
  { id: 'futaCredit', label: 'FUTA Credit Reduction' },
  { id: 'sutaTaxable', label: 'SUTA Taxable Wages' },
  { id: 'sutaTax', label: 'SUTA Tax' }
];
const PayrollReport = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedClient, setSelectedClient]: any = useState(null);
  const [selectedStartMonth, setSelectedStartMonth] = useState(null);
  const [selectedEndMonth, setSelectedEndMonth] = useState(null);
  const [payrolls, setPayrolls] = useState([]);
  const [selectedPayroll, setSelectedPayroll] = useState([]);
  const [isViewPayrollModal, setIsViewPayrolModal] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [reportType, setReportType] = useState('Summary');

  const styles = {
    cell: {
      border: '1px solid #E3E1D9',
      borderCollapse: 'collapse',
      padding: '6px'
    },
    cellTotal: {
      border: '1px solid #E3E1D9',
      borderCollapse: 'collapse',
      padding: '6px',
      fontWeight: 'bold'
    }
  };

  const handleClientChange = async (value) => {
    setSelectedClient(value);
  };

  const handleClear = () => {
    setSelectedClient(null);
    // setSelectedYear(null);
    setSelectedStartMonth(null);
    setSelectedEndMonth(null);
    setPayrolls([]);
  };

  const handleReportTypeChange = (event) => {
    setReportType(event.target.value);
  };

  const getDetailedRows = () => {
    const detailedData = {};

    payrolls.forEach((monthlyPayroll) => {
      monthlyPayroll.employeePayrolls.forEach((emp) => {
        const id = emp.federalTaxId || emp.employeeName;
        if (!detailedData[id]) {
          detailedData[id] = {
            ...emp,
            grossWages: 0,
            socSec: 0,
            medicare: 0,
            federalWH: 0,
            stateWH: 0,
            localWH: 0,
            tips: 0,
            benefitDeduction: 0,
            netPay: 0,
            futaTaxable: 0,
            futaTax: 0,
            futaCredit: 0,
            sutaTaxable: 0,
            sutaTax: 0
          };
        }

        detailedData[id].grossWages += parseFloat(emp.grossWages) || 0;
        detailedData[id].socSec += parseFloat(emp.socSec) || 0;
        detailedData[id].medicare += parseFloat(emp.medicare) || 0;
        detailedData[id].federalWH += parseFloat(emp.federalWH) || 0;
        detailedData[id].stateWH += parseFloat(emp.stateWH) || 0;
        detailedData[id].localWH += parseFloat(emp.localWH) || 0;
        detailedData[id].tips += parseFloat(emp.tips) || 0;
        detailedData[id].benefitDeduction +=
          parseFloat(emp.benefitDeduction) || 0;
        detailedData[id].netPay += parseFloat(emp.netPay) || 0;
        detailedData[id].futaTaxable += parseFloat(emp.futaTaxable) || 0;
        detailedData[id].futaTax += parseFloat(emp.futaTax) || 0;
        detailedData[id].futaCredit += parseFloat(emp.futaCredit) || 0;
        detailedData[id].sutaTaxable += parseFloat(emp.sutaTaxable) || 0;
        detailedData[id].sutaTax += parseFloat(emp.sutaTax) || 0;
      });
    });

    return Object.values(detailedData).map((row: any) => ({
      ...row,
      grossWages: row.grossWages.toFixed(2),
      socSec: row.socSec.toFixed(2),
      medicare: row.medicare.toFixed(2),
      federalWH: row.federalWH.toFixed(2),
      stateWH: row.stateWH.toFixed(2),
      localWH: row.localWH.toFixed(2),
      tips: row.tips.toFixed(2),
      benefitDeduction: row.benefitDeduction.toFixed(2),
      netPay: row.netPay.toFixed(2),
      futaTaxable: row.futaTaxable.toFixed(2),
      futaTax: row.futaTax.toFixed(2),
      futaCredit: row.futaCredit.toFixed(2),
      sutaTaxable: row.sutaTaxable.toFixed(2),
      sutaTax: row.sutaTax.toFixed(2)
    }));
  };

  const getClients = async () => {
    try {
      const data = await getPayrollClients();
      console.log('data::', data);

      // Filter out duplicate clients by id. If id is missing, fall back to clientId or name.
      const uniqueClients = (data.data || []).filter(
        (client: any, index: number, self: any[]) =>
          index ===
          self.findIndex(
            (c) => (c.id || c.clientId) === (client.id || client.clientId)
          )
      );

      setClients(uniqueClients);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };
  const getPayrolls = async () => {
    if (!selectedClient || !selectedStartMonth || !selectedEndMonth) {
      NotificationManager.error(
        `Please select all the required fields`,
        'Error',
        3000
      );
      return;
    }
    setLoading(true);
    console.log({ selectedClient, selectedStartMonth, selectedEndMonth });

    const payrollsResponse = await getClientWisePayrollsByParams({
      clientId: selectedClient?.id || 0,
      monthStart: moment(selectedStartMonth).format('YYYY-MM') || '0',
      monthEnd: moment(selectedEndMonth).format('YYYY-MM') || '0'
    });

    const sortedResponse = sortByYearAndMonth(payrollsResponse.data);
    setPayrolls(sortedResponse);

    setLoading(false);
  };

  const sortByYearAndMonth = (data: any) => {
    return data.sort((a, b) => {
      // Compare years first
      const yearDifference = a.year - b.year;
      if (yearDifference !== 0) {
        return yearDifference;
      }
      // If years are the same, compare months
      return a.month - b.month;
    });
  };

  function handlePayrollView(index: number): void {
    try {
      setLoading(true);
      setSelectedPayroll(payrolls[index].employeePayrolls);
      setIsViewPayrolModal(true);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  }
  const closeModal = () => {
    setIsViewPayrolModal(false);
    getPayrolls();
  };

  const handleExportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Styled Sheet');

    worksheet.addRow([]); // Add an empty row
    worksheet.addRow([
      `Legal Name: ${
        (selectedClient && selectedClient.legalBusinessName) || ''
      }`
    ]); // Row for Legal Business Name
    worksheet.addRow([
      `DBA: ${(selectedClient && selectedClient.doingBusinessAs) || ''}`
    ]); // Row for DBA
    worksheet.addRow([
      `Location:  ${(selectedClient && selectedClient.streetAddress1) || ''}, ${
        (selectedClient && selectedClient.city) || ''
      }, ${(selectedClient && selectedClient.state) || ''}, ${
        (selectedClient && selectedClient.zipCode) || ''
      } `
    ]); // Row for Location
    worksheet.addRow([
      `Report Period:  ${moment(selectedStartMonth).format(
        'MMM-YY'
      )} to ${moment(selectedEndMonth).format('MMM-YY')}`
    ]); // Add an empty row
    worksheet.addRow([]); // Add an empty row

    if (reportType === 'Summary') {
      worksheet.addRow([
        'Month/Year',
        'Number of Employees',
        'Gross Wages',
        'Soc. Sec.',
        'Medicare',
        'Federal WH',
        '941 Tax',
        'FUTA Tax',
        'State WH',
        'SUTA Tax'
      ]);
    } else {
      worksheet.addRow(detailedColumns.map((col) => col.label));
    }

    const legalName = worksheet.getCell('A2');
    const dba = worksheet.getCell('A3');
    const location = worksheet.getCell('A4');
    const reportPeriod = worksheet.getCell('A5');

    legalName.font = { bold: true, color: { argb: 'ff1c4e80' } };
    dba.font = { bold: true, color: { argb: 'ff1c4e80' } };
    location.font = { bold: true, color: { argb: 'ff1c4e80' } };
    reportPeriod.font = { bold: true, color: { argb: 'ff1c4e80' } };

    // Add color to complere row 7
    worksheet.getRow(7).eachCell((cell, colNumber) => {
      cell.font = { bold: true, color: { argb: 'ff1c4e80' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'ffc6eob4' }
      };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });

    worksheet.columns = [
      { width: 10 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 15 }
    ];

    worksheet.mergeCells('A2:E2'); // merging for Legal Business Name
    worksheet.mergeCells('A3:E3'); // merging for DBA
    worksheet.mergeCells('A4:E4'); // merging for Location
    worksheet.mergeCells('A5:E5'); // merging for Report Period

    // Add some data rows with different styles
    const data = [];
    if (reportType === 'Summary') {
      payrolls.map((row) => {
        data.push([
          `${moment(new Date(`${row.month}-01-${row.year}`)).format(
            'MMM-YYYY'
          )}`,
          row.employeePayrolls && +row.employeePayrolls.length,
          row.employeePayrolls &&
            +row.employeePayrolls
              .reduce(
                (total, payroll) => total + parseFloat(payroll.grossWages),
                0
              )
              .toFixed(2),
          row.employeePayrolls &&
            +row.employeePayrolls
              .reduce((total, payroll) => total + parseFloat(payroll.socSec), 0)
              .toFixed(2),
          row.employeePayrolls &&
            +row.employeePayrolls
              .reduce(
                (total, payroll) => total + parseFloat(payroll.medicare),
                0
              )
              .toFixed(2),
          row.employeePayrolls &&
            +row.employeePayrolls
              .reduce(
                (total, payroll) => total + parseFloat(payroll.federalWH),
                0
              )
              .toFixed(2),
          row.employeePayrolls &&
            +(
              (row.employeePayrolls.reduce(
                (total, payroll) => total + parseFloat(payroll.socSec),
                0
              ) +
                row.employeePayrolls.reduce(
                  (total, payroll) => total + parseFloat(payroll.medicare),
                  0
                )) *
                2 +
              row.employeePayrolls.reduce(
                (total, payroll) => total + parseFloat(payroll.federalWH),
                0
              )
            ).toFixed(2),
          row.employeePayrolls &&
            +row.employeePayrolls
              .reduce(
                (total, payroll) => total + parseFloat(payroll.futaTax),
                0
              )
              .toFixed(2),
          row.employeePayrolls &&
            +row.employeePayrolls
              .reduce(
                (total, payroll) => total + parseFloat(payroll.stateWH),
                0
              )
              .toFixed(2),
          row.employeePayrolls &&
            +row.employeePayrolls
              .reduce(
                (total, payroll) => total + parseFloat(payroll.sutaTax),
                0
              )
              .toFixed(2)
        ]);
      });

      data.push([
        'Total',
        +payrolls
          .reduce((sum, row) => sum + +row.employeePayrolls.length, 0)
          .toFixed(2),

        +payrolls
          .reduce(
            (sum, row) =>
              sum +
              +(
                row.employeePayrolls &&
                +row.employeePayrolls.reduce(
                  (total, payroll) => total + parseFloat(payroll.grossWages),
                  0
                )
              ),
            0
          )
          .toFixed(2),

        +payrolls
          .reduce(
            (sum, row) =>
              sum +
              +(
                row.employeePayrolls &&
                +row.employeePayrolls
                  .reduce(
                    (total, payroll) => total + parseFloat(payroll.socSec),
                    0
                  )
                  .toFixed(2)
              ),
            0
          )
          .toFixed(2),

        +payrolls
          .reduce(
            (sum, row) =>
              sum +
              +(
                row.employeePayrolls &&
                +row.employeePayrolls.reduce(
                  (total, payroll) => total + parseFloat(payroll.medicare),
                  0
                )
              ),
            0
          )
          .toFixed(2),

        +payrolls
          .reduce(
            (sum, row) =>
              sum +
              +(
                row.employeePayrolls &&
                +row.employeePayrolls.reduce(
                  (total, payroll) => total + parseFloat(payroll.federalWH),
                  0
                )
              ),
            0
          )
          .toFixed(2),

        +payrolls
          .reduce(
            (sum, row) =>
              sum +
              +(
                row.employeePayrolls &&
                +(
                  (row.employeePayrolls.reduce(
                    (total, payroll) => total + parseFloat(payroll.socSec),
                    0
                  ) +
                    row.employeePayrolls.reduce(
                      (total, payroll) => total + parseFloat(payroll.medicare),
                      0
                    )) *
                    2 +
                  row.employeePayrolls.reduce(
                    (total, payroll) => total + parseFloat(payroll.federalWH),
                    0
                  )
                )
              ),
            0
          )
          .toFixed(2),

        +payrolls
          .reduce(
            (sum, row) =>
              sum +
              +(
                row.employeePayrolls &&
                +row.employeePayrolls.reduce(
                  (total, payroll) => total + parseFloat(payroll.futaTax),
                  0
                )
              ),
            0
          )
          .toFixed(2),

        +payrolls
          .reduce(
            (sum, row) =>
              sum +
              +(
                row.employeePayrolls &&
                +row.employeePayrolls.reduce(
                  (total, payroll) => total + parseFloat(payroll.stateWH),
                  0
                )
              ),
            0
          )
          .toFixed(2),

        +payrolls
          .reduce(
            (sum, row) =>
              sum +
              +(
                row.employeePayrolls &&
                +row.employeePayrolls.reduce(
                  (total, payroll) => total + parseFloat(payroll.sutaTax),
                  0
                )
              ),
            0
          )
          .toFixed(2)
      ]);
    } else {
      const detailedRows = getDetailedRows();
      detailedRows.forEach((row) => {
        data.push(detailedColumns.map((col) => row[col.id]));
      });

      data.push([
        'Total',
        '',
        +detailedRows
          .reduce((sum, row) => sum + parseFloat(row.grossWages), 0)
          .toFixed(2),
        +detailedRows
          .reduce((sum, row) => sum + parseFloat(row.socSec), 0)
          .toFixed(2),
        +detailedRows
          .reduce((sum, row) => sum + parseFloat(row.medicare), 0)
          .toFixed(2),
        +detailedRows
          .reduce((sum, row) => sum + parseFloat(row.federalWH), 0)
          .toFixed(2),
        +detailedRows
          .reduce((sum, row) => sum + parseFloat(row.stateWH), 0)
          .toFixed(2),
        +detailedRows
          .reduce((sum, row) => sum + parseFloat(row.localWH), 0)
          .toFixed(2),
        +detailedRows
          .reduce((sum, row) => sum + parseFloat(row.tips), 0)
          .toFixed(2),
        +detailedRows
          .reduce((sum, row) => sum + parseFloat(row.benefitDeduction), 0)
          .toFixed(2),
        +detailedRows
          .reduce((sum, row) => sum + parseFloat(row.netPay), 0)
          .toFixed(2),
        +detailedRows
          .reduce((sum, row) => sum + parseFloat(row.futaTaxable), 0)
          .toFixed(2),
        +detailedRows
          .reduce((sum, row) => sum + parseFloat(row.futaTax), 0)
          .toFixed(2),
        +detailedRows
          .reduce((sum, row) => sum + parseFloat(row.futaCredit), 0)
          .toFixed(2),
        +detailedRows
          .reduce((sum, row) => sum + parseFloat(row.sutaTaxable), 0)
          .toFixed(2),
        +detailedRows
          .reduce((sum, row) => sum + parseFloat(row.sutaTax), 0)
          .toFixed(2)
      ]);
    }

    data.forEach((item) => {
      const dataRow = worksheet.addRow(item);
      dataRow.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      });
    });

    // Generate buffer and save the file
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer]),
      `${formatClientId(
        selectedClient?.id || 0
      )}-Payroll-${reportType}-${moment(selectedStartMonth).format(
        'MMM-YY'
      )}-${moment(selectedEndMonth).format('MMM-YY')}.xlsx`
    );
  };
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      await getClients();
    };
    fetchInitialData();
  }, []);

  return (
    <>
      <Box my={2} mx={5}>
        {loading && (
          <div className="loader-wrapper" style={{ textAlign: 'center' }}>
            <BarLoader color={'#1976D2'} loading={loading} width={'94vw'} />
          </div>
        )}
        <Typography
          sx={{
            textAlign: 'center',
            fontWeight: 'bold',
            color: '#1976D2',
            fontSize: '1.5em',
            my: 2
          }}
        >
          Payroll Summary
        </Typography>
        <Grid container spacing={2} mt={0}>
          <Grid item xs={12} md={3.5}>
            <InputLabel htmlFor="client">Client</InputLabel>
            <Autocomplete
              id="client"
              options={clients}
              getOptionLabel={(client: any) =>
                formatClientLabel(client, clients)
              }
              isOptionEqualToValue={(option: any, value: any) =>
                option.id === value.id
              }
              renderOption={(props, option: any) => (
                <li {...props} key={option.id}>
                  {formatClientLabel(option, clients)}
                </li>
              )}
              value={selectedClient}
              onChange={(_, newValue) => handleClientChange(newValue)}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" size="small" />
              )}
            />
          </Grid>
          <Grid item xs={6} md={1.5} mt={0}>
            <InputLabel htmlFor="startMonth">Start Month</InputLabel>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ width: '100%' }}>
                <DatePicker
                  views={['year', 'month']}
                  openTo="year"
                  value={selectedStartMonth}
                  inputFormat="MMM yyyy"
                  onChange={setSelectedStartMonth}
                  renderInput={(props) => (
                    <TextField {...props} style={{ width: '100%' }} />
                  )}
                  InputProps={{
                    size: 'small',
                    name: 'startMonth'
                  }}
                />
              </Box>
            </LocalizationProvider>
          </Grid>
          <Grid item xs={6} md={1.5} mt={0}>
            <InputLabel htmlFor="endMonth">End Month</InputLabel>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ width: '100%' }}>
                <DatePicker
                  views={['year', 'month']}
                  openTo="year"
                  value={selectedEndMonth}
                  inputFormat="MMM yyyy"
                  onChange={setSelectedEndMonth}
                  renderInput={(props) => (
                    <TextField {...props} style={{ width: '100%' }} />
                  )}
                  InputProps={{
                    size: 'small',
                    name: 'endMonth'
                  }}
                />
              </Box>
            </LocalizationProvider>
          </Grid>
          <Grid item xs={3.5} sm={3} md={1} sx={{ mt: { md: 3 } }}>
            <Button
              variant="contained"
              color="warning"
              onClick={handleClear}
              sx={{ scale: { xs: '0.8', md: '1' } }}
            >
              Clear
            </Button>
          </Grid>
          <Grid item xs={4} sm={2} md={1} sx={{ mt: { md: 3 } }}>
            <Button
              variant="contained"
              color="primary"
              onClick={getPayrolls}
              sx={{ scale: { xs: '0.8', md: '1' } }}
            >
              Search
              <SearchIcon />
            </Button>
          </Grid>
          <Grid item xs={3.8} sm={2} md={1} sx={{ mt: { md: 3 } }}>
            <Button
              variant="contained"
              color="success"
              onClick={handleExportToExcel}
              sx={{ scale: { xs: '0.8', md: '1' } }}
            >
              Export
              <FileDownloadIcon />
            </Button>
          </Grid>
        </Grid>
        <Grid container mt={1}>
          <Grid item xs={4} md={3}>
            <ToggleButtonGroup
              size="small"
              value={reportType}
              exclusive
              onChange={handleReportTypeChange}
            >
              <ToggleButton
                value="Summary"
                style={{
                  backgroundColor:
                    reportType === 'Summary' ? '#1976d2' : 'white',
                  color: reportType === 'Summary' ? 'white' : 'black',
                  border: '1px solid #ccc'
                }}
              >
                Summary
              </ToggleButton>
              <ToggleButton
                value="Detailed"
                style={{
                  backgroundColor:
                    reportType === 'Detailed' ? '#1976d2' : 'white',
                  color: reportType === 'Detailed' ? 'white' : 'black',
                  border: '1px solid #ccc'
                }}
              >
                Detailed
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>
      </Box>
      <Box mx={5} mt={3}>
        <Paper sx={{ width: '96vw', overflow: 'hidden' }} elevation={3}>
          <TableContainer sx={{ width: '100%', overflow: 'auto' }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {(reportType === 'Summary' ? columns : detailedColumns).map(
                    (column) => (
                      <TableCell
                        key={column.id}
                        style={{
                          minWidth: column.minWidth,
                          fontWeight: '800',
                          color: '#1976D2',
                          border: '1px solid #E3E1D9',
                          borderCollapse: 'collapse',
                          padding: 5
                        }}
                      >
                        {/* <TableSortLabel>{column.label}</TableSortLabel> */}
                        {column.label}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {(reportType === 'Summary'
                  ? payrolls.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage
                    )
                  : getDetailedRows().slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage
                    )
                ).map((row: any, index) => (
                  <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                    {reportType === 'Summary' ? (
                      <>
                        <TableCell sx={styles.cell}>
                          {formatClientId(+row.clientId)}
                        </TableCell>
                        <TableCell sx={styles.cell}>
                          {row.client && row.client.state}
                        </TableCell>
                        <TableCell sx={styles.cell}>
                          {row.client && row.client.legalBusinessName}
                        </TableCell>
                        <TableCell sx={styles.cell}>
                          {row.client && row.client.doingBusinessAs}
                        </TableCell>
                        <TableCell sx={styles.cell}>{`${moment(
                          new Date(`${row.month}-01-${row.year}`)
                        ).format('MMM-YYYY')}`}</TableCell>
                        <TableCell sx={styles.cell}>
                          {row.employeePayrolls && row.employeePayrolls.length}
                        </TableCell>
                        <TableCell sx={styles.cell}>
                          {row.employeePayrolls &&
                            row.employeePayrolls
                              .reduce(
                                (total, payroll) =>
                                  total + parseFloat(payroll.grossWages),
                                0
                              )
                              .toFixed(2)}
                        </TableCell>
                        <TableCell sx={styles.cell}>
                          {row.employeePayrolls &&
                            row.employeePayrolls
                              .reduce(
                                (total, payroll) =>
                                  total + parseFloat(payroll.socSec),
                                0
                              )
                              .toFixed(2)}
                        </TableCell>
                        <TableCell sx={styles.cell}>
                          {row.employeePayrolls &&
                            row.employeePayrolls
                              .reduce(
                                (total, payroll) =>
                                  total + parseFloat(payroll.medicare),
                                0
                              )
                              .toFixed(2)}
                        </TableCell>
                        <TableCell sx={styles.cell}>
                          {row.employeePayrolls &&
                            row.employeePayrolls
                              .reduce(
                                (total, payroll) =>
                                  total + parseFloat(payroll.federalWH),
                                0
                              )
                              .toFixed(2)}
                        </TableCell>
                        <TableCell sx={styles.cell}>
                          {row.employeePayrolls &&
                            (
                              (row.employeePayrolls.reduce(
                                (total, payroll) =>
                                  total + parseFloat(payroll.socSec),
                                0
                              ) +
                                row.employeePayrolls.reduce(
                                  (total, payroll) =>
                                    total + parseFloat(payroll.medicare),
                                  0
                                )) *
                                2 +
                              row.employeePayrolls.reduce(
                                (total, payroll) =>
                                  total + parseFloat(payroll.federalWH),
                                0
                              )
                            ).toFixed(2)}
                        </TableCell>
                        <TableCell sx={styles.cell}>
                          {row.employeePayrolls &&
                            row.employeePayrolls
                              .reduce(
                                (total, payroll) =>
                                  total + parseFloat(payroll.futaTax),
                                0
                              )
                              .toFixed(2)}
                        </TableCell>
                        <TableCell sx={styles.cell}>
                          {row.employeePayrolls &&
                            row.employeePayrolls
                              .reduce(
                                (total, payroll) =>
                                  total + parseFloat(payroll.stateWH),
                                0
                              )
                              .toFixed(2)}
                        </TableCell>
                        <TableCell sx={styles.cell}>
                          {row.employeePayrolls &&
                            row.employeePayrolls
                              .reduce(
                                (total, payroll) =>
                                  total + parseFloat(payroll.sutaTax),
                                0
                              )
                              .toFixed(2)}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-around',
                            // marginTop: '20px',
                            ...styles.cell
                          }}
                        >
                          <IconButton
                            // onClick={() => handleEdit(index)}
                            style={{ color: '#1976D2', cursor: 'pointer' }}
                          >
                            <VisibilityIcon
                              onClick={() => handlePayrollView(index)}
                              style={{ color: '#1976D2', cursor: 'pointer' }}
                            />
                          </IconButton>
                        </TableCell>
                      </>
                    ) : (
                      detailedColumns.map((col) => (
                        <TableCell key={col.id} sx={styles.cell}>
                          {row[col.id]}
                        </TableCell>
                      ))
                    )}
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell
                    sx={styles.cellTotal}
                    style={{ textAlign: 'center' }}
                    colSpan={reportType === 'Summary' ? 5 : 2}
                  >
                    Total
                  </TableCell>

                  {reportType === 'Summary' ? (
                    <>
                      <TableCell sx={styles.cellTotal}>
                        {
                          +payrolls
                            .reduce(
                              (sum, row) =>
                                sum + +(row.employeePayrolls?.length || 0),
                              0
                            )
                            .toFixed(2)
                        }
                      </TableCell>
                      <TableCell sx={styles.cellTotal}>
                        {
                          +payrolls
                            .reduce(
                              (sum, row) =>
                                sum +
                                +(
                                  row.employeePayrolls?.reduce(
                                    (t, p: any) =>
                                      t + parseFloat(p.grossWages || 0),
                                    0
                                  ) || 0
                                ),
                              0
                            )
                            .toFixed(2)
                        }
                      </TableCell>
                      <TableCell sx={styles.cellTotal}>
                        {
                          +payrolls
                            .reduce(
                              (sum, row) =>
                                sum +
                                +(
                                  row.employeePayrolls?.reduce(
                                    (t, p: any) =>
                                      t + parseFloat(p.socSec || 0),
                                    0
                                  ) || 0
                                ),
                              0
                            )
                            .toFixed(2)
                        }
                      </TableCell>
                      <TableCell sx={styles.cellTotal}>
                        {
                          +payrolls
                            .reduce(
                              (sum, row) =>
                                sum +
                                +(
                                  row.employeePayrolls?.reduce(
                                    (t, p: any) =>
                                      t + parseFloat(p.medicare || 0),
                                    0
                                  ) || 0
                                ),
                              0
                            )
                            .toFixed(2)
                        }
                      </TableCell>
                      <TableCell sx={styles.cellTotal}>
                        {
                          +payrolls
                            .reduce(
                              (sum, row) =>
                                sum +
                                +(
                                  row.employeePayrolls?.reduce(
                                    (t, p: any) =>
                                      t + parseFloat(p.federalWH || 0),
                                    0
                                  ) || 0
                                ),
                              0
                            )
                            .toFixed(2)
                        }
                      </TableCell>
                      <TableCell sx={styles.cellTotal}>
                        {
                          +payrolls
                            .reduce((sum, row) => {
                              const socio =
                                row.employeePayrolls?.reduce(
                                  (t, p: any) => t + parseFloat(p.socSec || 0),
                                  0
                                ) || 0;
                              const medi =
                                row.employeePayrolls?.reduce(
                                  (t, p: any) =>
                                    t + parseFloat(p.medicare || 0),
                                  0
                                ) || 0;
                              const fed =
                                row.employeePayrolls?.reduce(
                                  (t, p: any) =>
                                    t + parseFloat(p.federalWH || 0),
                                  0
                                ) || 0;
                              return sum + (socio + medi) * 2 + fed;
                            }, 0)
                            .toFixed(2)
                        }
                      </TableCell>
                      <TableCell sx={styles.cellTotal}>
                        {
                          +payrolls
                            .reduce(
                              (sum, row) =>
                                sum +
                                +(
                                  row.employeePayrolls?.reduce(
                                    (t, p: any) =>
                                      t + parseFloat(p.futaTax || 0),
                                    0
                                  ) || 0
                                ),
                              0
                            )
                            .toFixed(2)
                        }
                      </TableCell>
                      <TableCell sx={styles.cellTotal}>
                        {
                          +payrolls
                            .reduce(
                              (sum, row) =>
                                sum +
                                +(
                                  row.employeePayrolls?.reduce(
                                    (t, p: any) =>
                                      t + parseFloat(p.stateWH || 0),
                                    0
                                  ) || 0
                                ),
                              0
                            )
                            .toFixed(2)
                        }
                      </TableCell>
                      <TableCell sx={styles.cellTotal}>
                        {
                          +payrolls
                            .reduce(
                              (sum, row) =>
                                sum +
                                +(
                                  row.employeePayrolls?.reduce(
                                    (t, p: any) =>
                                      t + parseFloat(p.sutaTax || 0),
                                    0
                                  ) || 0
                                ),
                              0
                            )
                            .toFixed(2)
                        }
                      </TableCell>
                      <TableCell sx={styles.cellTotal}></TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell sx={styles.cellTotal}>
                        {
                          +getDetailedRows()
                            .reduce(
                              (sum, row) =>
                                sum + parseFloat(row.grossWages || 0),
                              0
                            )
                            .toFixed(2)
                        }
                      </TableCell>
                      <TableCell sx={styles.cellTotal}>
                        {
                          +getDetailedRows()
                            .reduce(
                              (sum, row) => sum + parseFloat(row.socSec || 0),
                              0
                            )
                            .toFixed(2)
                        }
                      </TableCell>
                      <TableCell sx={styles.cellTotal}>
                        {
                          +getDetailedRows()
                            .reduce(
                              (sum, row) => sum + parseFloat(row.medicare || 0),
                              0
                            )
                            .toFixed(2)
                        }
                      </TableCell>
                      <TableCell sx={styles.cellTotal}>
                        {
                          +getDetailedRows()
                            .reduce(
                              (sum, row) =>
                                sum + parseFloat(row.federalWH || 0),
                              0
                            )
                            .toFixed(2)
                        }
                      </TableCell>
                      <TableCell sx={styles.cellTotal}>
                        {
                          +getDetailedRows()
                            .reduce(
                              (sum, row) => sum + parseFloat(row.stateWH || 0),
                              0
                            )
                            .toFixed(2)
                        }
                      </TableCell>
                      <TableCell sx={styles.cellTotal}>
                        {
                          +getDetailedRows()
                            .reduce(
                              (sum, row) => sum + parseFloat(row.localWH || 0),
                              0
                            )
                            .toFixed(2)
                        }
                      </TableCell>
                      <TableCell sx={styles.cellTotal}>
                        {
                          +getDetailedRows()
                            .reduce(
                              (sum, row) => sum + parseFloat(row.tips || 0),
                              0
                            )
                            .toFixed(2)
                        }
                      </TableCell>
                      <TableCell sx={styles.cellTotal}>
                        {
                          +getDetailedRows()
                            .reduce(
                              (sum, row) =>
                                sum + parseFloat(row.benefitDeduction || 0),
                              0
                            )
                            .toFixed(2)
                        }
                      </TableCell>
                      <TableCell sx={styles.cellTotal}>
                        {
                          +getDetailedRows()
                            .reduce(
                              (sum, row) => sum + parseFloat(row.netPay || 0),
                              0
                            )
                            .toFixed(2)
                        }
                      </TableCell>
                      <TableCell sx={styles.cellTotal}>
                        {
                          +getDetailedRows()
                            .reduce(
                              (sum, row) =>
                                sum + parseFloat(row.futaTaxable || 0),
                              0
                            )
                            .toFixed(2)
                        }
                      </TableCell>
                      <TableCell sx={styles.cellTotal}>
                        {
                          +getDetailedRows()
                            .reduce(
                              (sum, row) => sum + parseFloat(row.futaTax || 0),
                              0
                            )
                            .toFixed(2)
                        }
                      </TableCell>
                      <TableCell sx={styles.cellTotal}>
                        {
                          +getDetailedRows()
                            .reduce(
                              (sum, row) =>
                                sum + parseFloat(row.futaCredit || 0),
                              0
                            )
                            .toFixed(2)
                        }
                      </TableCell>
                      <TableCell sx={styles.cellTotal}>
                        {
                          +getDetailedRows()
                            .reduce(
                              (sum, row) =>
                                sum + parseFloat(row.sutaTaxable || 0),
                              0
                            )
                            .toFixed(2)
                        }
                      </TableCell>
                      <TableCell sx={styles.cellTotal}>
                        {
                          +getDetailedRows()
                            .reduce(
                              (sum, row) => sum + parseFloat(row.sutaTax || 0),
                              0
                            )
                            .toFixed(2)
                        }
                      </TableCell>
                    </>
                  )}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[50, 100, 200]}
            count={
              reportType === 'Summary'
                ? payrolls.length
                : getDetailedRows().length
            }
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>
      <ViewPayroll
        open={isViewPayrollModal}
        onClose={closeModal}
        payroll={selectedPayroll}
      />
    </>
  );
};

export default PayrollReport;
